import { useCallback } from "react";
import { useOverlays } from "contexts/OverlayContext";
import { SchedulePeriodLockedError } from "@api/scheduleErrors";
import { useAccruedLockedStartToast } from "./useAccruedLockedStartToast";

/** Takes a caught error, so it stays `unknown` until narrowed. */
export type ShowLockedPeriods = (error: unknown) => boolean;

/**
 * Routes a locked-period refusal to the global dialog instead of a toast.
 * Returns whether the error was one, so a call site can fall through to its
 * ordinary error handling otherwise. Surfaces inside an overlay pass a local
 * dialog, which wins over the global one. A locked back-dated accrued start
 * is the exception: it gets a toast wherever it happens.
 */
export function useScheduleLockedPeriodsDialog(
    localDialog?: ShowLockedPeriods,
): ShowLockedPeriods {
    const { openOverlay } = useOverlays();
    const showAccruedLockedStart = useAccruedLockedStartToast();

    return useCallback(
        (error: unknown): boolean => {
            if (showAccruedLockedStart(error)) {
                return true;
            }
            if (localDialog) {
                return localDialog(error);
            }
            if (!(error instanceof SchedulePeriodLockedError)) {
                return false;
            }
            openOverlay("scheduleLockedPeriods", error.details);
            return true;
        },
        [localDialog, openOverlay, showAccruedLockedStart],
    );
}
